import { CheckCircle2, HelpCircle, Package, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ExpectedFormatCardData } from '@/components/ai/ExpectedFormatCard';

type SupportLevel =
  | 'STRONG_SUPPORT'
  | 'PARTIAL_SUPPORT'
  | 'WEAK_SUPPORT'
  | 'NO_SUPPORT'
  | 'INSUFFICIENT_EVIDENCE';

type RiskBand = 'LOW' | 'MEDIUM' | 'HIGH';

export interface VerificationSummaryData {
  id: number;
  postingEventId: number;
  engineVersion: string;
  overallVerificationRisk: number;
  riskBand: RiskBand;
  primaryReasonFlags: string[];
  assessmentSummary: string | null;
  systemSignals: {
    expected_format_support: SupportLevel;
    quantity_support: SupportLevel;
    image_quality: 'ACCEPTABLE' | 'POOR' | 'UNCLEAR';
    evidence_sufficiency: 'SUFFICIENT' | 'INSUFFICIENT';
    overall_verification_risk: number;
    risk_band: RiskBand;
    primary_reason_flags: string[];
    assessment_summary: string;
    inputs_state: 'TRIAGE_ONLY' | 'AI_VERIFICATION_ONLY' | 'TRIAGE_AND_AI_VERIFICATION' | 'MINIMAL_FALLBACK';
  };
}

interface Props {
  verificationSummary?: VerificationSummaryData | null;
  formatContext?: ExpectedFormatCardData | null;
}

function supportTone(value: SupportLevel): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (value === 'STRONG_SUPPORT') return 'default';
  if (value === 'PARTIAL_SUPPORT') return 'secondary';
  if (value === 'WEAK_SUPPORT' || value === 'INSUFFICIENT_EVIDENCE') return 'outline';
  return 'destructive';
}

function riskTone(value: RiskBand): 'default' | 'secondary' | 'destructive' {
  if (value === 'LOW') return 'default';
  if (value === 'MEDIUM') return 'secondary';
  return 'destructive';
}

function supportLabel(value: SupportLevel): string {
  return value.replace(/_/g, ' ');
}

function formatInputState(value: VerificationSummaryData['systemSignals']['inputs_state']): string {
  return value.replace(/_/g, ' ');
}

export default function VerificationSummaryPanel({ verificationSummary, formatContext }: Props) {
  if (!verificationSummary) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            System Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Verification summary is not available for this submission yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  const signals = verificationSummary.systemSignals;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            System Assessment
          </span>
          <Badge variant={riskTone(verificationSummary.riskBand)}>
            {verificationSummary.riskBand} RISK
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {formatContext?.expectedFormatName ? (
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                  <Package className="h-3.5 w-3.5" />
                  Expected Format
                </div>
                <p className="text-lg font-semibold leading-tight">{formatContext.expectedFormatName}</p>
                {formatContext.reviewerDescription ? (
                  <p className="text-sm text-muted-foreground">{formatContext.reviewerDescription}</p>
                ) : null}
              </div>
              {typeof formatContext.expectedQuantity === 'number' ? (
                <Badge variant="secondary">Qty {formatContext.expectedQuantity}</Badge>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="rounded-lg border">
          <div className="grid gap-0 sm:grid-cols-2">
            <div className="border-b p-4 sm:border-b sm:border-r">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Expected Format Support</div>
              <div className="mt-2">
                <Badge variant={supportTone(signals.expected_format_support)}>
                  {supportLabel(signals.expected_format_support)}
                </Badge>
              </div>
            </div>
            <div className="border-b p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Quantity Support</div>
              <div className="mt-2">
                <Badge variant={supportTone(signals.quantity_support)}>
                  {supportLabel(signals.quantity_support)}
                </Badge>
              </div>
            </div>
            <div className="border-b p-4 sm:border-b-0 sm:border-r">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Image Quality</div>
              <div className="mt-2 flex items-center gap-2 text-sm font-medium">
                <span>{signals.image_quality}</span>
                {signals.image_quality === 'ACCEPTABLE' ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : null}
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Evidence Sufficiency</div>
              <div className="mt-2 space-y-1">
                <p className="text-sm font-medium">{signals.evidence_sufficiency}</p>
                <p className="text-xs text-muted-foreground">Built from {formatInputState(signals.inputs_state)}</p>
              </div>
            </div>
          </div>
        </div>

        {verificationSummary.primaryReasonFlags.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Reason Flags</div>
            <div className="flex flex-wrap gap-2">
              {verificationSummary.primaryReasonFlags.map((flag) => (
                <Badge key={flag} variant="outline">{flag.replace(/_/g, ' ')}</Badge>
              ))}
            </div>
          </div>
        ) : null}

        {(verificationSummary.assessmentSummary || signals.assessment_summary) ? (
          <p className="text-sm text-muted-foreground">
            {verificationSummary.assessmentSummary || signals.assessment_summary}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
